/**
 * Downscales an image file to specified dimensions while maintaining quality.
 * Useful for optimizing avatar/logo display without loading full-resolution images.
 * Uses step-down scaling for better quality on large images.
 *
 * @param file - The image file or blob to downscale
 * @param width - Target width in pixels
 * @param height - Target height in pixels
 * @param quality - Image quality (0-1), default 0.9 for high quality
 * @returns Promise resolving to base64 data URL of the downscaled image
 */
export async function downscaleImage(
  file: File | Blob,
  width: number,
  height: number,
  quality: number = 0.9
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      // Clean up object URL to prevent memory leaks
      URL.revokeObjectURL(url);

      // Calculate cropping dimensions for object-cover behavior (center crop to square)
      const size = Math.min(img.width, img.height);
      const sourceX = (img.width - size) / 2;
      const sourceY = (img.height - size) / 2;

      // Only downscale if image is larger than 1.5x the target size
      // Otherwise use original size (don't upscale, and don't downscale small differences)
      const shouldDownscale = size > width * 1.5;
      const targetWidth = shouldDownscale ? width : size;
      const targetHeight = shouldDownscale ? height : size;

      // Use step-down scaling for better quality when scaling down large images
      let canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return reject(new Error('Canvas context not available'));
      }

      // Calculate scaling steps for better quality
      // If image is much larger, scale in steps rather than all at once
      const scalingRatio = Math.min(targetWidth / size, targetHeight / size);
      const shouldUseStepDownScaling = scalingRatio < 0.5;

      if (shouldUseStepDownScaling) {
        // Step 1: Scale to 2x the target size first
        const intermediateWidth = targetWidth * 2;
        const intermediateHeight = targetHeight * 2;

        canvas.width = intermediateWidth;
        canvas.height = intermediateHeight;

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Draw cropped center square to intermediate size
        ctx.drawImage(
          img,
          sourceX,
          sourceY,
          size,
          size,
          0,
          0,
          intermediateWidth,
          intermediateHeight
        );

        // Step 2: Scale from intermediate to final size
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = targetWidth;
        tempCanvas.height = targetHeight;
        const tempCtx = tempCanvas.getContext('2d');

        if (!tempCtx) {
          return reject(new Error('Canvas context not available'));
        }

        tempCtx.imageSmoothingEnabled = true;
        tempCtx.imageSmoothingQuality = 'high';
        tempCtx.drawImage(canvas, 0, 0, targetWidth, targetHeight);

        canvas = tempCanvas;
      } else {
        // Direct scaling for smaller images
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        // Draw cropped center square
        ctx.drawImage(img, sourceX, sourceY, size, size, 0, 0, targetWidth, targetHeight);
      }

      // Convert to Blob URL instead of base64 data URL
      // Blob URLs keep image data in browser-managed memory (~10x less JS heap usage)
      const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      canvas.toBlob(
        blob => {
          if (!blob) {
            return reject(new Error('Canvas toBlob returned null'));
          }
          resolve(URL.createObjectURL(blob));
        },
        mimeType,
        quality
      );
    };

    img.onerror = err => {
      URL.revokeObjectURL(url);
      reject(err);
    };

    img.src = url;
  });
}

/**
 * Gets the appropriate downscale dimensions based on avatar size.
 * Returns larger dimensions for higher quality rendering.
 *
 * @param size - Avatar size ('xs' | 'sm' | 'md' | 'lg' | 'xl')
 * @returns Object with width and height in pixels
 */
export function getAvatarDownscaleDimensions(size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'): {
  width: number;
  height: number;
} {
  // Use larger dimensions for high-quality rendering
  // 4x the display size for crisp, sharp images
  const sizeMap = {
    xs: 4 * 24,
    sm: 4 * 32,
    md: 4 * 40,
    lg: 4 * 48,
    xl: 4 * 64,
  };

  const dimension = sizeMap[size];

  return { width: dimension, height: dimension };
}
